<template>
  <div class="msg-page">
    <div class="container-fluid py-4 px-4">

      <nav aria-label="breadcrumb" class="mb-1">
        <ol class="breadcrumb">
          <li class="breadcrumb-item"><router-link to="/">الرئيسية</router-link></li>
          <li class="breadcrumb-item"><router-link to="/messages">إدارة الرسائل</router-link></li>
          <li class="breadcrumb-item active"><span class="breadcrumb-current">{{ meta.name }}</span></li>
        </ol>
      </nav>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-5">
        <i class="pi pi-spin pi-spinner" style="font-size:2rem; color:var(--gold)"></i>
        <p class="text-muted mt-2">جاري تحميل القالب...</p>
      </div>

      <!-- Not found -->
      <div v-else-if="!template" class="text-center py-5">
        <i class="pi pi-exclamation-triangle" style="font-size:2.5rem; color:#f59e0b;"></i>
        <p class="mt-3 text-muted">لم يتم العثور على القالب المطلوب</p>
        <router-link to="/messages" class="btn-tpl-back mt-2">
          <i class="pi pi-arrow-right"></i>
          العودة للقوالب
        </router-link>
      </div>

      <template v-else>

        <!-- Page header -->
        <div class="page-header mb-4">
          <div class="d-flex align-items-center gap-3">
            <div class="edit-page-icon">
              <i class="pi" :class="meta.icon"></i>
            </div>
            <div>
              <h1 class="page-header__title">{{ meta.name }}</h1>
              <p class="page-header__subtitle">{{ meta.desc }}</p>
            </div>
          </div>
          <span
            class="status-badge"
            :class="section.isActive ? 'status-badge--accepted' : 'status-badge--rejected'"
          >
            {{ section.isActive ? 'مفعّل' : 'معطّل' }}
          </span>
        </div>

        <div class="row g-4">

          <!-- Editor -->
          <div class="col-lg-7">
            <div class="msg-card">
              <div class="msg-card__head">
                <i class="pi pi-pencil"></i>
                <span>تحرير القالب</span>
              </div>
              <div class="msg-card__body">

                <div class="var-section" v-if="meta.vars.length">
                  <p class="var-section__label">انقر على متغير لإدراجه في مكان المؤشر:</p>
                  <div class="var-chips">
                    <button
                      v-for="v in meta.vars"
                      :key="v.key"
                      class="var-chip"
                      type="button"
                      @click="insertVar(v.key)"
                      :title="v.label"
                    >
                      <i class="pi pi-code"></i>{{ wv(v.key) }}
                    </button>
                  </div>
                </div>

                <div class="mt-3">
                  <label class="edit-field-label">نص الرسالة</label>
                  <textarea
                    ref="editor"
                    v-model="section.message"
                    class="msg-textarea"
                    rows="12"
                    dir="rtl"
                    placeholder="اكتب نص الرسالة هنا..."
                    @input="section.isDirty = true"
                  ></textarea>
                  <p class="char-count">{{ section.message.length }} حرف</p>
                </div>

              </div>
              <div class="msg-card__footer">
                <label class="toggle-label">
                  <input type="checkbox" v-model="section.isActive" @change="section.isDirty = true" />
                  <span class="toggle-switch"></span>
                  <span class="toggle-text">{{ section.isActive ? 'مفعّل' : 'معطّل' }}</span>
                </label>
                <div class="d-flex gap-2">
                  <button class="btn-reset" type="button" @click="reset" :disabled="!section.isDirty">
                    <i class="pi pi-refresh"></i>
                    استعادة
                  </button>
                  <button class="btn-save" type="button" @click="save" :disabled="section.saving || !section.isDirty">
                    <i class="pi" :class="section.saving ? 'pi-spin pi-spinner' : 'pi-check'"></i>
                    {{ section.saving ? 'جاري الحفظ...' : 'حفظ التغييرات' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Preview -->
          <div class="col-lg-5">
            <div class="msg-card h-100">
              <div class="msg-card__head">
                <i class="pi pi-eye"></i>
                <span>معاينة مباشرة</span>
                <span class="ms-auto badge-preview">بيانات تجريبية</span>
              </div>
              <div class="msg-card__body d-flex flex-column align-items-center justify-content-center" style="min-height:280px;">
                <div class="wa-phone-wrap">
                  <div class="wa-phone-header">
                    <div class="wa-avatar"><i class="pi pi-user"></i></div>
                    <div>
                      <div class="wa-contact-name">ولي أمر أحمد</div>
                      <div class="wa-contact-status">online</div>
                    </div>
                  </div>
                  <div class="wa-chat-area">
                    <div class="wa-bubble">
                      <p class="wa-bubble__text" dir="rtl">{{ preview }}</p>
                      <span class="wa-bubble__time">10:32 ✓✓</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </template>

    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

const TEMPLATE_VARS = {
  absence_notification: [
    { key: 'student_name',    label: 'اسم الطالب' },
    { key: 'parent_name',     label: 'اسم ولي الأمر' },
    { key: 'parent_phone',    label: 'هاتف ولي الأمر' },
    { key: 'attendance_date', label: 'تاريخ الغياب' },
    { key: 'attendance_time', label: 'وقت الغياب' },
    { key: 'grade_name',      label: 'المرحلة الدراسية' },
    { key: 'teacher_name',    label: 'اسم المعلم' },
  ],
  absence_notification_bulk: [
    { key: 'parent_name',     label: 'اسم ولي الأمر' },
    { key: 'student_name',    label: 'اسم الطالب' },
    { key: 'parent_phone',    label: 'هاتف ولي الأمر' },
    { key: 'attendance_date', label: 'تاريخ الغياب' },
    { key: 'attendance_time', label: 'وقت الغياب' },
    { key: 'grade_name',      label: 'المرحلة الدراسية' },
    { key: 'teacher_name',    label: 'اسم المعلم' },
  ],
  payment_confirmation: [
    { key: 'student_name', label: 'اسم الطالب' },
    { key: 'parent_name',  label: 'اسم ولي الأمر' },
    { key: 'parent_phone', label: 'هاتف ولي الأمر' },
    { key: 'fee_name',     label: 'اسم البند المالي' },
    { key: 'fee_amount',   label: 'مبلغ الرسوم' },
    { key: 'grade_name',   label: 'المرحلة الدراسية' },
    { key: 'teacher_name', label: 'اسم المعلم' },
    { key: 'date',         label: 'التاريخ' },
    { key: 'time',         label: 'الوقت' },
  ],
  payment_reminder_bulk: [
    { key: 'parent_name',      label: 'اسم ولي الأمر' },
    { key: 'student_name',     label: 'اسم الطالب' },
    { key: 'parent_phone',     label: 'هاتف ولي الأمر' },
    { key: 'payment_name',     label: 'اسم البند المالي' },
    { key: 'payment_amount',   label: 'مبلغ الرسوم' },
    { key: 'payment_due_date', label: 'تاريخ الاستحقاق' },
    { key: 'academic_year',    label: 'العام الدراسي' },
    { key: 'grade_name',       label: 'المرحلة الدراسية' },
    { key: 'teacher_name',     label: 'اسم المعلم' },
  ],
  reservation_notification: [
    { key: 'student_name', label: 'اسم الطالب' },
    { key: 'parent_name',  label: 'اسم ولي الأمر' },
    { key: 'parent_phone', label: 'هاتف ولي الأمر' },
    { key: 'grade_name',   label: 'المرحلة الدراسية' },
    { key: 'teacher_name', label: 'اسم المعلم' },
  ],
  attendance_confirmation: [
    { key: 'student_name', label: 'اسم الطالب' },
    { key: 'teacher_name', label: 'اسم المعلم' },
    { key: 'date',         label: 'التاريخ' },
    { key: 'time',         label: 'الوقت' },
  ],
};

const TEMPLATE_META = {
  payment_confirmation: {
    name: 'تأكيد السداد',
    desc: 'تُرسل لولي الأمر فور تسجيل دفع الطالب',
    icon: 'pi-check-circle',
  },
  absence_notification: {
    name: 'تنبيه غياب فردي',
    desc: 'تُرسل لولي الأمر عند تسجيل غياب الطالب عن الحصة',
    icon: 'pi-calendar-times',
  },
  absence_notification_bulk: {
    name: 'إشعار غياب جماعي',
    desc: 'تُرسل دفعة واحدة لأولياء أمور الغائبين من صفحة الحضور',
    icon: 'pi-users',
  },
  payment_reminder_bulk: {
    name: 'تذكير جماعي بالسداد',
    desc: 'تُرسل لجميع غير المسددين دفعة واحدة من صفحة المدفوعات',
    icon: 'pi-bell',
  },
  reservation_notification: {
    name: 'رسالة الحجز',
    desc: 'تُرسل لولي الأمر عند تسجيل حجز جديد للطالب',
    icon: 'pi-bookmark',
  },
  attendance_confirmation: {
    name: 'تأكيد الحضور',
    desc: 'تُرسل لولي الأمر عند تسجيل حضور الطالب بنجاح',
    icon: 'pi-check',
  },
};

const SAMPLE = {
  student_name:     'أحمد محمد علي',
  parent_name:      'محمد علي',
  parent_phone:     '01012345678',
  attendance_date:  '٢٤ يونيو ٢٠٢٦',
  attendance_time:  '١٠:٣٠ ص',
  grade_name:       'الصف الثالث الإعدادي',
  teacher_name:     'مصطفى حسني',
  fee_name:         'مصاريف شهر يناير',
  fee_amount:       '500',
  payment_name:     'مصاريف شهر يناير',
  payment_amount:   '500',
  payment_due_date: '٣٠ يونيو ٢٠٢٦',
  academic_year:    'ثالثة إعدادي',
  date:             '٢٤ يونيو ٢٠٢٦',
  time:             '١٠:٣٠ ص',
};

const TOAST = Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 3000, timerProgressBar: true });

export default {
  name: 'MessagesEdit',
  props: {
    templateCode: { type: String, required: true },
  },
  data() {
    return {
      loading: true,
      template: null,
      section: {
        message:  '',
        original: '',
        isActive: true,
        isDirty:  false,
        saving:   false,
      },
    };
  },
  computed: {
    meta() {
      const base = TEMPLATE_META[this.templateCode] ?? { name: this.templateCode, desc: '', icon: 'pi-envelope' };
      return { ...base, vars: TEMPLATE_VARS[this.templateCode] ?? [] };
    },
    preview() {
      let text = this.section.message || '';
      for (const [k, v] of Object.entries(SAMPLE)) {
        text = text.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), v);
      }
      return text || 'اكتب الرسالة لرؤية المعاينة هنا...';
    },
  },
  methods: {
    wv(key) { return '{{' + key + '}}'; },

    insertVar(key) {
      const ta    = this.$refs.editor;
      if (!ta) return;
      const text  = `{{${key}}}`;
      const start = ta.selectionStart;
      const end   = ta.selectionEnd;
      this.section.message = this.section.message.substring(0, start) + text + this.section.message.substring(end);
      this.section.isDirty = true;
      this.$nextTick(() => {
        ta.selectionStart = ta.selectionEnd = start + text.length;
        ta.focus();
      });
    },

    async loadTemplate() {
      this.loading = true;
      try {
        const res  = await axios.get('/whatsapp-templates');
        const list = res.data.data ?? [];
        const tpl  = list.find(t => t.code === this.templateCode);
        this.template = tpl ?? null;
        if (tpl) {
          this.section.message  = tpl.message;
          this.section.original = tpl.message;
          this.section.isActive = tpl.is_active;
          this.section.isDirty  = false;
        }
      } catch {
        TOAST.fire({ icon: 'error', title: 'فشل تحميل القالب' });
      } finally {
        this.loading = false;
      }
    },

    async save() {
      if (!this.template) return;
      this.section.saving = true;
      try {
        await axios.put(
          `/whatsapp-templates/${this.template.id}`,
          { message: this.section.message, is_active: this.section.isActive },
          { headers: { 'Content-Type': 'application/json' } }
        );
        this.section.original = this.section.message;
        this.section.isDirty  = false;
        TOAST.fire({ icon: 'success', title: 'تم حفظ القالب بنجاح' });
      } catch (e) {
        const hint = e?.response?.data?.message || '';
        TOAST.fire({ icon: 'error', title: hint ? `فشل الحفظ: ${hint}` : 'فشل الحفظ، حاول مرة أخرى' });
      } finally {
        this.section.saving = false;
      }
    },

    reset() {
      this.section.message = this.section.original;
      this.section.isDirty = false;
    },
  },
  mounted() { this.loadTemplate(); },
};
</script>

<style>
@import './messages.css';

.edit-page-icon {
  width: 48px;
  height: 48px;
  border-radius: var(--radius-md);
  background: var(--gold-light);
  border: 1px solid rgba(245, 166, 35, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.edit-page-icon .pi {
  color: var(--gold);
  font-size: 1.375rem;
}

.edit-field-label {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--text-secondary);
  display: block;
  margin-bottom: 0.5rem;
}

.btn-tpl-back {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.45rem 1rem;
  background: var(--gold);
  color: var(--btn-on-gold, #0F0F0F);
  border: none;
  border-radius: var(--radius-full);
  font-size: 0.8125rem;
  font-weight: 700;
  text-decoration: none;
  transition: all var(--ease-base);
}

.btn-tpl-back:hover {
  background: var(--gold-dark);
  color: var(--btn-on-gold, #0F0F0F);
}
</style>
